<script setup>
import { ref, computed } from 'vue';
import LoadingSpinner from '../LoadingSpinner.vue';

const isOpen = ref(false);
const message = ref('');
const isSubmitting = ref(false);
const touched = ref(false);
const errors = ref({});

const whatsappNumber = '+60183609178'; // Replace with actual WhatsApp number

const validateMessage = () => {
  const value = message.value.trim();
  errors.value = {};

  if (touched.value && !value) {
    errors.value.message = 'Please enter a message';
  } else if (value && value.length < 5) {
    errors.value.message = 'Message must be at least 5 characters';
  } else if (value && value.length > 500) {
    errors.value.message = 'Message must be less than 500 characters';
  }

  return Object.keys(errors.value).length === 0;
};

const messageLength = computed(() => message.value.length);
const isValid = computed(() => validateMessage());
const canSubmit = computed(() => isValid.value && message.value.trim() && !isSubmitting.value);

const handleInput = () => {
  touched.value = true;
  validateMessage();
};

const sendMessage = async () => {
  if (!canSubmit.value) return;

  isSubmitting.value = true;

  try {
    const encodedMessage = encodeURIComponent(message.value);
    window.open(`https://wa.me/${whatsappNumber}?text=${encodedMessage}`, '_blank');

    // Simulate a delay for UX
    await new Promise(resolve => setTimeout(resolve, 1000));

    message.value = '';
    touched.value = false;
    errors.value = {};
    isOpen.value = false;
  } catch (error) {
    console.error('Error sending message:', error);
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div class="fixed z-50 bottom-6 right-6">
    <!-- Chat Window -->
    <div 
      v-if="isOpen"
      class="absolute right-0 mb-4 transition-transform duration-300 origin-bottom-right transform bg-white rounded-lg shadow-xl bottom-16 w-80"
      :class="isOpen ? 'scale-100' : 'scale-0'"
    >
      <!-- Header -->
      <div class="flex items-center justify-between p-4 bg-green-500 rounded-t-lg">
        <div class="flex items-center gap-3">
          <img src="/icons/senyumlogo.png" alt="Senyum Dental" class="w-10 h-10 rounded-full">
          <div class="text-white">
            <h3 class="font-semibold">Senyum Dental</h3>
            <p class="text-xs opacity-90">Typically replies instantly</p>
          </div>
        </div>
        <button 
          @click="isOpen = false"
          class="p-1 text-white rounded hover:bg-green-600"
        >
          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Chat Body -->
      <div class="p-4 bg-gray-50">
        <div class="bg-green-100 p-3 rounded-lg inline-block max-w-[90%] text-sm">
          👋 Hi! How can we help you today?
        </div>
      </div>

      <!-- Input Area -->
      <div class="p-4 border-t">
        <form @submit.prevent="sendMessage" class="flex flex-col gap-3">
          <!-- Message input -->
          <div class="relative">
            <label for="whatsapp-message" class="sr-only">Your message</label>
            <textarea
              id="whatsapp-message"
              v-model="message"
              @input="handleInput"
              type="text"
              :disabled="isSubmitting"
              placeholder="Type your message..."
              rows="3"
              maxlength="500"
              class="w-full px-3 py-2 text-sm border rounded-lg resize-none focus:outline-none focus:border-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              :class="{
                'border-red-300 focus:border-red-500': errors.message,
                'border-gray-300': !errors.message
              }"
            ></textarea>

            <!-- Character counter -->
            <div class="flex justify-between items-center mt-1 text-xs">
              <span
                class="text-gray-500"
                :class="{ 'text-red-500': messageLength > 450 }"
              >
                {{ messageLength }}/500
              </span>
              <span v-if="errors.message" class="text-red-500">
                {{ errors.message }}
              </span>
            </div>
          </div>

          <!-- Submit button -->
          <button
            type="submit"
            :disabled="!canSubmit"
            class="w-full px-4 py-2 text-white transition-colors bg-green-500 rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-green-500 flex items-center justify-center gap-2"
          >
            <LoadingSpinner
              v-if="isSubmitting"
              size="small"
              color="white"
            />
            <span v-else>Send Message</span>
          </button>
        </form>
      </div>
    </div>

    <!-- Toggle Button -->
    <button
      @click="isOpen = !isOpen"
      class="p-4 text-white transition-colors bg-green-500 rounded-full shadow-lg hover:bg-green-600"
    >
      <svg 
        class="w-6 h-6"
        fill="currentColor"
        viewBox="0 0 24 24"
      >
        <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
      </svg>
    </button>
  </div>
</template>